import { useEffect, useRef, useState } from 'react';
import { Copy, Eye, EyeOff, KeyRound, Shuffle, X } from 'lucide-react';

interface Props {
  mode: 'create' | 'edit';
  initialLabel?: string;
  initialValue?: string;
  /**
   * External error surfaced by the parent (e.g. peer deleted the field
   * before our update transaction landed). When set, the modal stays open
   * and shows a "Copy your value" button so the user can rescue the secret
   * they just typed instead of losing it to an auto-dismissing toast.
   */
  externalError?: string | null;
  onSave: (label: string, value: string) => void;
  onClose: () => void;
}

/**
 * Masked insert/edit dialog for password fields. Replaces the browser
 * `prompt()` which has no masking, no validation, and renders plaintext
 * in the OS-level dialog.
 */
export function PasswordEditorModal({
  mode,
  initialLabel = '',
  initialValue = '',
  externalError = null,
  onSave,
  onClose,
}: Props) {
  const [label, setLabel] = useState(initialLabel);
  const [value, setValue] = useState(initialValue);
  const [showValue, setShowValue] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const labelRef = useRef<HTMLInputElement>(null);

  async function copyValue() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy to clipboard.');
    }
  }

  useEffect(() => {
    labelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function generate() {
    // 20 chars, alphanumeric + symbols, cryptographically random.
    const alphabet =
      'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*_-=+';
    const bytes = new Uint8Array(20);
    crypto.getRandomValues(bytes);
    let out = '';
    for (let i = 0; i < 20; i++) out += alphabet[bytes[i]! % alphabet.length];
    setValue(out);
    setShowValue(true);
  }

  function save() {
    if (!label.trim()) {
      setError('Label is required.');
      return;
    }
    if (!value) {
      setError('Password value is required.');
      return;
    }
    onSave(label.trim(), value);
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
      <div className="card w-full max-w-md p-5">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound className="text-dragon-500" size={18} />
          <h2 className="font-semibold">
            {mode === 'create' ? 'Insert password field' : 'Edit password field'}
          </h2>
          <button onClick={onClose} className="btn-ghost ml-auto !p-1">
            <X size={16} />
          </button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-ink-400">Label</label>
            <input
              ref={labelRef}
              className="input mt-1"
              placeholder="e.g. Admin Password, SSH Root Key"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              onKeyDown={(e) => {
                if (e.key === 'Enter') save();
              }}
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-ink-400">Password</label>
            <div className="mt-1 flex gap-1">
              {/* This field holds a credential going INTO the document, not
                  the user's own login. Suppress password-manager autofill
                  AND save-prompts — `autoComplete="new-password"` alone
                  triggers save dialogs in most PMs. The data-* attrs are
                  the canonical opt-outs for 1Password / LastPass / Bitwarden
                  / Dashlane; `data-form-type="other"` covers the generic
                  heuristic. The non-`password`-keyword `name` defeats
                  filename-based capture too. */}
              <input
                className="input flex-1 font-mono"
                type={showValue ? 'text' : 'password'}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                name="dragonhq-doc-secret"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                data-1p-ignore="true"
                data-lpignore="true"
                data-bwignore="true"
                data-dashlane-ignore="true"
                data-form-type="other"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') save();
                }}
              />
              <button
                type="button"
                className="btn-ghost !p-2"
                onClick={() => setShowValue((v) => !v)}
                title={showValue ? 'Hide' : 'Show'}
              >
                {showValue ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
              <button
                type="button"
                className="btn-ghost !p-2"
                onClick={generate}
                title="Generate strong password"
              >
                <Shuffle size={14} />
              </button>
            </div>
          </div>
          {error && <div className="text-sm text-red-400">{error}</div>}
          {externalError && (
            <div className="text-sm text-red-400 space-y-2">
              <div>{externalError}</div>
              {value && (
                <button
                  type="button"
                  className="btn-ghost !py-1 !px-2 inline-flex items-center gap-1.5 text-xs"
                  onClick={copyValue}
                  title="Copy this password to your clipboard so you don't lose it"
                >
                  <Copy size={12} /> {copied ? 'Copied' : 'Copy your value'}
                </button>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-ghost" onClick={onClose}>
              {externalError ? 'Discard' : 'Cancel'}
            </button>
            <button type="button" className="btn-primary" onClick={save}>
              {mode === 'create' ? 'Insert' : 'Save'}
            </button>
          </div>
          <p className="text-xs text-ink-500">
            The password is encrypted client-side with the vault key before it leaves your browser.
            Revealing it later always requires re-entering your Master Code.
          </p>
        </div>
      </div>
    </div>
  );
}
